import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Catalogue, CatalogueSharingMode } from "../../types/catalogue.types";
import BasicDetailsTab from "./tabs/BasicDetailsTab";
import ProductsTab from "./tabs/ProductsTab";
import PricingTab from "./tabs/PricingTab";
import PerformanceTab from "./tabs/PerformanceTab";
import InquiriesTab from "./tabs/InquiriesTab";
import SettingsTab from "./tabs/SettingsTab";
import ActivityTab, { type ActivityFollowUpTarget } from "./tabs/ActivityTab";
import { useStudioPageChromeOverride } from "../../common/StudioPageChromeContext";
import ShareInviteModal from "./ShareInviteModal";
import notifications from "../../common/Components/Notifications/Notification";
import { catalogueAddFromInventoryPath } from "./cataloguePaths";
import {
  defaultCataloguePriceTier,
  type CataloguePriceTier,
} from "./cataloguePricing";
import { catalogueDisplayName } from "./catalogueSystemManaged";

type TabKey = 'basic' | 'products' | 'pricing' | 'performance' | 'inquiries' | 'settings' | 'activity';

interface CatalogueDetailProps {
  catalogue: Catalogue;
  onBack: () => void;
  onExportData?: () => void;
  exporting?: boolean;
  initialTab?: TabKey;
  initialInquiryId?: string;
  initialSubmissionId?: string;
  initialLinkToken?: string;
}

const CATALOGUE_TABS = [
  { key: 'basic' as TabKey, label: 'Basic Details', shortLabel: 'Details' },
  { key: 'products' as TabKey, label: 'Products', shortLabel: 'Products' },
  { key: 'pricing' as TabKey, label: 'Pricing', shortLabel: 'Pricing' },
  { key: 'performance' as TabKey, label: 'Performance', shortLabel: 'Performance' },
  { key: 'inquiries' as TabKey, label: 'Customer Inquiries', shortLabel: 'Inquiries' },
  { key: 'settings' as TabKey, label: 'Settings', shortLabel: 'Settings' },
  { key: 'activity' as TabKey, label: 'Activity', shortLabel: 'Activity' },
];

const CatalogueDetail: React.FC<CatalogueDetailProps> = ({
  catalogue,
  onBack,
  onExportData,
  exporting,
  initialTab,
  initialInquiryId,
  initialSubmissionId,
  initialLinkToken,
}) => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab || 'basic');
  const [shareOpen, setShareOpen] = useState(false);
  const [priceTier, setPriceTier] = useState<CataloguePriceTier>(defaultCataloguePriceTier);
  const [inquiryFocusId, setInquiryFocusId] = useState<string | undefined>(initialInquiryId);
  const [submissionFocusId, setSubmissionFocusId] = useState<string | undefined>(initialSubmissionId);
  const [linkFocusToken, setLinkFocusToken] = useState<string | undefined>(initialLinkToken);

  useEffect(() => {
    setActiveTab(initialTab || 'basic');
    setInquiryFocusId(initialInquiryId);
    setSubmissionFocusId(initialSubmissionId);
    setLinkFocusToken(initialLinkToken);
  }, [catalogue.id, initialTab, initialInquiryId, initialSubmissionId, initialLinkToken]);

  useEffect(() => {
    setPriceTier(defaultCataloguePriceTier());
  }, [catalogue.id]);

  const tabs = useMemo(() => CATALOGUE_TABS, []);

  const audiencePreviewPath = `/catalogue-products/${catalogue.id}`;
  // Sharing mode can change in Settings or the share modal; every tab reads it from here.
  const [sharingMode, setSharingMode] = useState<CatalogueSharingMode | undefined>(catalogue.sharingMode);
  useEffect(() => setSharingMode(catalogue.sharingMode), [catalogue.id, catalogue.sharingMode]);
  const currentCatalogue = useMemo(() => ({ ...catalogue, sharingMode }), [catalogue, sharingMode]);

  const openAudiencePreview = async () => {
    const apiBase = process.env.REACT_APP_SERVERURL || "";
    const openTab = () =>
      window.open(
        `${window.location.origin}${audiencePreviewPath}`,
        "_blank",
        "noopener,noreferrer"
      );

    if (!apiBase) {
      openTab();
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(
        `${apiBase}/catalogue-security/${catalogue.id}/public-status`,
        { headers }
      );
      const json = await res.json();
      if (!json?.success) {
        notifications({
          title: "Preview",
          message: json?.message || "Could not verify catalogue visibility.",
          type: "warning",
        });
        return;
      }
      const st = json.data;
      if (st?.isExpired) {
        notifications({
          title: "Catalogue expired",
          message: "Viewers can no longer open this catalogue.",
          type: "warning",
        });
        return;
      }
      if (st?.maxViewsReached) {
        notifications({
          title: "View limit reached",
          message: "This catalogue has reached its maximum views.",
          type: "warning",
        });
        return;
      }
      openTab();
    } catch {
      notifications({
        title: "Preview",
        message: "Could not verify status; opening the public catalogue page.",
        type: "info",
      });
      openTab();
    }
  };

  useStudioPageChromeOverride({
    title: catalogueDisplayName(catalogue),
    onBack,
    tabs,
    activeTab,
    onTabChange: (key) => {
      setActiveTab(key as TabKey);
      if (key !== "inquiries") {
        setInquiryFocusId(undefined);
        setSubmissionFocusId(undefined);
      }
    },
    actions: [
      ...(onExportData
        ? [
            {
              key: "export",
              label: exporting ? "Exporting…" : "Export",
              onClick: onExportData,
              disabled: Boolean(exporting),
            },
          ]
        : []),
      {
        key: "preview",
        label: "Preview",
        onClick: () => {
          void openAudiencePreview();
        },
      },
      {
        key: "share",
        label: "Share",
        onClick: () => setShareOpen(true),
        variant: "primary" as const,
      },
    ],
  });

  const openAddFromInventory = () => {
    history.push({
      pathname: catalogueAddFromInventoryPath(catalogue.id),
      state: { catalogue },
    });
  };

  const openCustomerFollowUp = (target: ActivityFollowUpTarget) => {
    setInquiryFocusId(target.inquiryId);
    setSubmissionFocusId(target.submissionId);
    setActiveTab("inquiries");
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicDetailsTab
            catalogue={currentCatalogue}
            onBack={onBack}
            onRequestAddProducts={openAddFromInventory}
            onExportData={onExportData}
            exporting={exporting}
            onPreview={openAudiencePreview}
            onShare={() => setShareOpen(true)}
          />
        );
      case 'products':
        return (
          <ProductsTab
            catalogue={currentCatalogue}
            onAddFromInventory={openAddFromInventory}
            priceTier={priceTier}
            onPriceTierChange={setPriceTier}
          />
        );
      case 'pricing':
        return (
          <PricingTab
            catalogue={currentCatalogue}
            priceTier={priceTier}
            onPriceTierChange={setPriceTier}
          />
        );
      case 'performance':
        return <PerformanceTab catalogue={currentCatalogue} focusLinkToken={linkFocusToken} />;
      case 'inquiries':
        return (
          <InquiriesTab
            catalogue={currentCatalogue}
            focusInquiryId={inquiryFocusId}
            focusSubmissionId={submissionFocusId}
          />
        );
      case 'settings':
        return (
          <SettingsTab
            catalogue={currentCatalogue}
            onSharingModeChange={setSharingMode}
            onGoToPricing={() => setActiveTab("pricing")}
          />
        );
      case 'activity':
        return <ActivityTab catalogue={currentCatalogue} onOpenFollowUp={openCustomerFollowUp} />;
      default:
        return <BasicDetailsTab catalogue={currentCatalogue} onBack={onBack} />;
    }
  };

  return (
    <div className="catalogue-detail">
      <div className="catalogue-detail-body">{renderTabContent()}</div>

      <ShareInviteModal
        catalogue={currentCatalogue}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
};

export default CatalogueDetail;
